/* Shopify owns every price. Private properties identify only this add attempt,
   so error recovery cannot remove an unrelated item already in the basket. */
(() => {
  if (customElements.get('set-offer')) return;
  class SetOffer extends HTMLElement {
    static busy = false;
    connectedCallback() {
      this.disconnectedCallback();
      this.selects = Array.from(this.querySelectorAll('[data-offer-variant]'));
      this.button = this.querySelector('[data-offer-submit]');
      this.total = this.querySelector('[data-offer-total]');
      this.message = this.querySelector('[data-offer-message]');
      this.basket = this.querySelector('[data-offer-basket]');
      if (!this.button || this.selects.length !== 3) return;
      this.label ??= this.button.textContent;
      this.disclosure = this.querySelector('.set-offer__expand');
      this.disclosureSummary = this.disclosure?.querySelector('summary');
      this.onDisclosure = event => {
        event.preventDefault();
        if (this.disclosureAnimation) return;
        this.anchorStyle = document.documentElement.style.overflowAnchor;
        this.bodyAnchorStyle = document.body.style.overflowAnchor;
        document.documentElement.style.overflowAnchor = 'none';
        document.body.style.overflowAnchor = 'none';
        const closing = this.disclosure.open;
        const start = this.disclosure.getBoundingClientRect().height;
        const collapsed = this.disclosureSummary.getBoundingClientRect().height + 1;
        if (!closing) this.disclosure.open = true;
        const end = closing ? collapsed : this.disclosure.getBoundingClientRect().height;
        if (!this.disclosure.animate || matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.disclosure.open = !closing;
          this.restoreScrollAnchoring();
          return;
        }
        this.disclosure.style.overflow = 'hidden';
        this.disclosureAnimation = this.disclosure.animate([{height: `${start}px`}, {height: `${end}px`}], {duration: 240, easing: 'cubic-bezier(.25,.1,.25,1)'});
        this.disclosureAnimation.onfinish = () => {
          this.disclosure.open = !closing;
          this.disclosure.style.overflow = '';
          this.disclosureAnimation = null;
          this.restoreScrollAnchoring();
        };
      };
      this.disclosureSummary?.addEventListener('click', this.onDisclosure);
      const mainSelect = this.selects[0];
      this.mainHolder = mainSelect.closest('[data-offer-card]');
      if (this.dataset.showMain === 'true' && this.dataset.mainDefault !== 'true') {
        this.mainHolder.hidden = false;
        this.mainHome = this.mainHolder.parentElement;
        this.querySelector('.set-offer__main-description')?.append(this.mainHolder);
        mainSelect.removeAttribute('tabindex');
      }
      this.change = event => {
        if (event.target === mainSelect && this.dataset.showMain === 'true' && !this.syncingMain) this.syncMainProduct();
        this.update();
      };
      this.click = () => this.submit();
      this.addEventListener('change', this.change);
      this.button.addEventListener('click', this.click);
      if (this.closest('.set-offer--inline')) this.pickerCleanup = this.selects.filter(select => (!select.hasAttribute('data-offer-main') || this.dataset.showMain === 'true') && select.options.length > 1).map(select => {
        return (this.dataset.selectorStyle === 'separate' && window.asenmoOfferVariantPicker?.(select)) || window.asenmoOptionMenu?.(select);
      }).filter(Boolean);
      if (this.dataset.productSection && typeof subscribe === 'function') {
        this.variantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({data}) => {
          if (data?.sectionId !== this.dataset.productSection) return;
          const main = this.querySelector('[data-offer-main]');
          if (main) { this.syncingMain = true; main.value = String(data.variant?.id || ''); main.dispatchEvent(new Event('change', {bubbles:true})); this.syncingMain = false; }
          this.variantPending = false;
          this.update();
        });
        this.optionUnsubscribe = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({data}) => {
          if (data?.target?.closest('product-info')?.dataset.section !== this.dataset.productSection) return;
          this.variantPending = true;
          this.update();
        });
      }
      this.update();
    }
    disconnectedCallback() {
      this.disclosureSummary?.removeEventListener('click', this.onDisclosure);
      this.disclosureAnimation?.cancel();
      this.restoreScrollAnchoring();
      this.disclosureAnimation = null;
      if (this.disclosure) this.disclosure.style.overflow = '';
      this.pickerCleanup?.forEach(cleanup => cleanup());
      this.pickerCleanup = [];
      if (this.mainHome && this.mainHolder) { this.mainHolder.hidden = true; this.mainHome.prepend(this.mainHolder); this.mainHome = null; }
      this.variantUnsubscribe?.();
      this.optionUnsubscribe?.();
      this.removeEventListener('change', this.change);
      this.button?.removeEventListener('click', this.click);
    }
    restoreScrollAnchoring() {
      if (this.anchorStyle == null) return;
      document.documentElement.style.overflowAnchor = this.anchorStyle;
      document.body.style.overflowAnchor = this.bodyAnchorStyle;
      this.anchorStyle = null;
      this.bodyAnchorStyle = null;
    }
    syncMainProduct() {
      let values;
      try {
        values = JSON.parse(this.selects[0].selectedOptions[0]?.dataset.optionValues || '[]');
      } catch (_) {
        return;
      }
      const picker = this.closest('product-info')?.querySelector('variant-selects');
      if (!picker || !values.length) return;
      const groups = [...picker.querySelectorAll('fieldset, .product-form__input--dropdown')];
      if (groups.length !== values.length) return;
      const inputs = groups.map((group, index) => {
        return [...group.querySelectorAll('input[type="radio"]')].find(input => input.value === values[index]) ||
          [...group.querySelectorAll('select')].find(select => [...select.options].some(option => option.value === values[index]));
      });
      if (inputs.some(input => !input)) return;
      let changedInput;
      inputs.forEach((input, index) => {
        const changed = input.type === 'radio' ? !input.checked : input.value !== values[index];
        if (input.type === 'radio') input.checked = true;
        else input.value = values[index];
        // Every dropdown must update its selected metadata before one variant request.
        picker.updateSelectionMetadata?.({ target: input });
        if (changed) changedInput = input;
      });
      if (changedInput) {
        this.variantPending = true;
        changedInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    text(key, fallback) { return this.getAttribute(`data-text-${key}`) || fallback; }
    choices() {
      return this.selects.map(select => {
        const option = select.selectedOptions[0];
        return { id: Number(select.value), product: select.dataset.product,
          price: Number(option?.dataset.price), role: select.dataset.role,
          available: !!option && !option.disabled && !select.disabled && Number(option.dataset.min || 1) <= 1 && Number(option.dataset.increment || 1) === 1 };
      });
    }
    format(price) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.dataset.currency || 'GBP'
      }).format(price / 100);
    }
    update() {
      const choices = this.choices();
      const complete = choices.every(x => x.id > 0 && Number.isFinite(x.price) && x.available);
      this.selects.forEach((select, index) => {
        const price = select.closest('[data-offer-card]').querySelector('[data-offer-price]');
        if (price && choices[index].id > 0) price.textContent = this.format(choices[index].price);
      });
      const main = this.selects[0].selectedOptions[0];
      const caption = this.querySelector('[data-offer-main-caption]');
      const mainPrice = this.querySelector('[data-offer-main-price]');
      if (caption) { caption.textContent = main?.textContent || ''; caption.hidden = this.dataset.mainDefault === 'true'; }
      if (mainPrice && choices[0].id) mainPrice.textContent = this.format(choices[0].price);
      this.selects.slice(0, 2).forEach((select, index) => {
        const image = this.querySelector(`[data-offer-preview="${index}"]`);
        const src = select.selectedOptions[0]?.dataset.image;
        if (image && src && src.includes('/')) { image.src = src; image.removeAttribute('srcset'); }
      });
      const previewTotal = this.querySelector('[data-offer-preview-total]');
      if (previewTotal) {
        const paidTotal = this.selects.slice(0, 2).reduce((sum, select) => {
          const option = select.value ? select.selectedOptions[0] : [...select.options].find(option => option.value && !option.disabled);
          return sum + Number(option?.dataset.price || 0);
        }, 0);
        previewTotal.textContent = this.format(paidTotal);
      }
      this.total.textContent = complete ? this.format(choices.filter(x => x.role === 'paid').reduce((sum, x) => sum + x.price, 0)) : this.text('pending-label', 'Choose options');
      this.button.disabled = this.loading || this.variantPending || this.reviewRequired || !complete || this.dataset.ready !== 'true' || this.dataset.sellable !== 'true';
      if (!this.loading) this.button.textContent = this.reviewRequired ? this.text('review-label', 'Review your basket below') : this.dataset.sellable !== 'true' ? this.text('unavailable-label', 'Set currently unavailable') : this.label;
    }
    async request(path, body) {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}${path}`, body ? {
        method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(body)
      } : {headers: {Accept: 'application/json'}, cache: 'no-store'});
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'The basket could not be updated.');
      return data;
    }
    own(cart) { return cart.items.filter(item => item.properties?._set_attempt === this.attempt); }
    qualified(cart, chosen) {
      const items = this.own(cart);
      return items.length === 3 && chosen.every(choice => items.some(item =>
        Number(item.variant_id) === choice.id && item.quantity === 1 &&
        item.properties?._set_role === choice.role &&
        (choice.role !== 'gift' || item.final_line_price === 0)));
    }
    async rollback(cart) {
      const items = this.own(cart);
      if (items.length) {
        await this.request('cart/update.js', {updates: Object.fromEntries(items.map(item => [item.key, 0]))});
      }
      const fresh = await this.request('cart.js');
      if (this.own(fresh).length) throw new Error('Basket recovery needs review.');
      return fresh;
    }
    show(text, review = false) {
      this.message.textContent = text;
      this.message.hidden = false;
      if (this.basket) this.basket.hidden = !review;
    }
    async refresh(cart, open = false) {
      try {
        if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, {source: 'set-offer', cartData: cart});
      } catch (_) { /* A third-party subscriber must not change purchase recovery. */ }
      if (!open) return;
      const drawer = document.querySelector('cart-drawer');
      if (!drawer?.renderContents) { if (window.asenmoCartFeedback?.()) return; window.location.href = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`; return; }
      try {
        const ids = drawer.getSectionsToRender().map(section => section.id);
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=${encodeURIComponent(ids.join(','))}`, {cache: 'no-store'});
        if (!response.ok) throw new Error('Unable to load basket');
        const sections = await response.json();
        if (!ids.every(id => typeof sections[id] === 'string')) throw new Error('Missing basket section');
        drawer.setActiveElement(this.button);
        drawer.classList.remove('is-empty');
        drawer.renderContents({sections});
      } catch (_) {
        // A rendering failure must never retry a successful purchase action.
        this.show(this.text('display-message', 'Your set and free gift are in the basket. Open it below to review.'), true);
      }
    }
    async submit() {
      if (SetOffer.busy || this.button.disabled) return;
      if (this.dataset.productSection) {
        const main = document.getElementById(`MainProduct-${this.dataset.productSection}`);
        if (main?.querySelector('.product-field [name^="properties["]')) {
          this.show(this.text('configuration-message', 'This product has additional options. Add it using the main product button, then choose the other items on their product pages.'), true);
          return;
        }
      }
      const chosen = this.choices();
      if (chosen.length !== 3 || chosen.some(x => !x.id || !x.available)) return;
      SetOffer.busy = true;
      this.loading = true;
      this.button.disabled = true;
      this.button.textContent = this.text('checking-label', 'Checking your set…');
      this.button.setAttribute('aria-busy', 'true');
      this.selects.forEach(select => { select.disabled = true; });
      let posted = false;
      let confirmed = false;
      try {
        const current = await this.request('cart.js');
        if (current.items.some(item => item.properties?._set_offer === this.dataset.offerId)) {
          this.show(this.text('duplicate-message', 'This offer is already in your basket. Review it there before adding another set.'), true);
          return;
        }
        this.attempt = crypto.randomUUID();
        const paid = chosen.filter(x => x.role === 'paid');
        posted = true;
        await this.request('cart/add.js', {items: chosen.map(choice => ({
          id: choice.id, quantity: 1, properties: {
            _set_offer: this.dataset.offerId, _set_attempt: this.attempt,
            _set_role: choice.role, _set_first: paid[0].product, _set_second: paid[1].product
          }
        }))});
        const cart = await this.request('cart.js');
        if (!this.qualified(cart, chosen)) {
          const restored = await this.rollback(cart);
          posted = false;
          this.show(this.text('discount-error-message', 'This gift offer is unavailable right now. The set was not kept in your basket. You can shop the items individually.'), true);
          await this.refresh(restored);
          return;
        }
        // From here the add is confirmed. Never roll it back for a UI failure.
        confirmed = true;
        posted = false;
        this.show(this.text('success-message', 'Set added. Your gift is free in the basket.'), true);
        await this.refresh(cart, true);
      } catch (error) {
        if (posted) {
          try {
            const recovered = await this.rollback(await this.request('cart.js'));
            await this.refresh(recovered);
            this.show(this.text('recovery-message', 'We could not complete this offer. Any items added by this attempt have been removed; please try again.'), true);
          } catch (_) {
            this.reviewRequired = true;
            this.show(this.text('review-message', 'We could not confirm the basket update. Please review your basket before trying again.'), true);
          }
        } else if (confirmed) {
          this.reviewRequired = true;
          this.show(this.text('display-error-message', 'Set added, but the basket display could not refresh. Open your basket to review it before adding again.'), true);
        } else {
          this.show(this.text('connection-message', 'We could not check your basket. Please try again.'), true);
        }
      } finally {
        SetOffer.busy = false;
        this.loading = false;
        this.selects.forEach(select => { select.disabled = false; });
        this.button.removeAttribute('aria-busy');
        this.update();
      }
    }
  }
  customElements.define('set-offer', SetOffer);
})();
